import { memo, ReactNode, useEffect, useState } from "react";
import {
  RedoButton,
  RedoButtonHierarchy,
  RedoButtonSize,
  RedoButtonTheme,
} from "../arbiter-components/buttons/redo-button";
import { RedoTextInput } from "../arbiter-components/input/redo-text-input";
import { Tooltip } from "../arbiter-components/tooltip/tooltip";
import { Flex } from "../flex";
import { Actions } from "../page";
import { Text } from "../text";
import { BgColorValue } from "../theme/box";

export type FormBuilderHeaderButton =
  | {
      text: string;
      onClick(): void | Promise<void>;
      hierarchy: RedoButtonHierarchy;
      theme?: RedoButtonTheme;
      disabled?: boolean;
      pending?: boolean;
      size?: RedoButtonSize;
      tooltip?: string;
      dataTrackId?: string;
    }
  | { customComponent: ReactNode };

export const FormBuilderHeader = memo(function FormBuilderHeader({
  title,
  onTitleChange,
  description,
  descriptionRender,
  buttons,
  additionalContent,
  autoSaveIcon,
  backgroundColor,
  centerContent,
  titleLeading,
  titleTrailing,
  compact,
}: {
  title: string;
  onTitleChange?: (title: string) => void;
  description?: string;
  descriptionRender?: ReactNode;
  buttons: FormBuilderHeaderButton[];
  additionalContent?: ReactNode;
  autoSaveIcon?: ReactNode;
  backgroundColor?: BgColorValue;
  centerContent?: ReactNode;
  /** Rendered directly to the left of the title. */
  titleLeading?: ReactNode;
  /** Rendered directly to the right of the title. */
  titleTrailing?: ReactNode;
  compact?: boolean;
}) {
  // Keep intermediate state while user is typing.
  // Only call onTitleChange when the input loses focus.
  const [currentTitle, setCurrentTitle] = useState(title);
  useEffect(() => {
    setCurrentTitle(title);
  }, [title]);

  return (
    <Flex
      bgColor={backgroundColor ?? "primary"}
      borderBottomWidth="1px"
      borderColor="primary"
      borderStyle="solid"
      dir="column"
    >
      <Flex
        align="center"
        gap="2xl"
        justify="space-between"
        pl="5xl"
        pr="3xl"
        py={compact ? "lg" : "3xl"}
        w="full"
      >
        <Flex align="center" gap="lg" shrink={0}>
          {titleLeading}
          <Flex dir="column" gap="md" shrink={0}>
            {onTitleChange ? (
              <RedoTextInput
                ariaLabel="Title"
                fullWidth
                onBlur={() => onTitleChange?.(currentTitle)}
                setValue={setCurrentTitle}
                size={compact ? "sm" : "lg"}
                value={currentTitle}
              />
            ) : (
              <Text fontSize={compact ? "sm" : "lg"} fontWeight="semibold">
                {title}
              </Text>
            )}
            {description && <Text>{description}</Text>}
            {descriptionRender && descriptionRender}
          </Flex>
          {titleTrailing}
        </Flex>
        {centerContent && (
          <Flex align="center" grow={1} justify="center">
            {centerContent}
          </Flex>
        )}
        <Flex align="center" shrink={0}>
          <Actions noBorder show>
            <Flex align="center" gap="md">
              {autoSaveIcon}
              <Flex>
                {buttons.map((button, idx) => {
                  if ("customComponent" in button) {
                    return (
                      <Flex align="center" justify="center" key={idx}>
                        {button.customComponent}
                      </Flex>
                    );
                  }

                  const buttonElement = (
                    <RedoButton
                      {...button}
                      size={button.size ?? "lg"}
                      theme={button.theme}
                    />
                  );

                  // Wrap with tooltip if tooltip text is provided
                  // Use a span wrapper to ensure tooltip works on disabled buttons
                  if (button.tooltip) {
                    return (
                      <Tooltip key={idx} title={button.tooltip}>
                        <span>{buttonElement}</span>
                      </Tooltip>
                    );
                  }

                  return <div key={idx}>{buttonElement}</div>;
                })}
              </Flex>
            </Flex>
          </Actions>
        </Flex>
      </Flex>
      {additionalContent}
    </Flex>
  );
});
